import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateAnforderung, useUpdateAnforderung } from '../../../api/hooks/useAnforderungen';
import { useAnforderungKategorieNamen } from '../../../api/hooks/useAnforderungKategorien';
import { useNotifications } from '../../../context/NotificationContext';
import { formatApiError } from '../../../api/client';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

const STATUSES = ['Neu', 'In Planung', 'Umgesetzt', 'Abgenommen'] as const;

interface FormData {
  role: string;
  ziel: string;
  nutzen: string;
  kategorie: string;
  story_points: string;
  status: typeof STATUSES[number];
}

const INITIAL: FormData = { role: '', ziel: '', nutzen: '', kategorie: '', story_points: '', status: 'Neu' };

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  projectId: string;
  initialData?: Record<string, unknown>;
  onClose: () => void;
}

export function AnforderungDialog({ open, mode, projectId, initialData, onClose }: Props) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const createMutation = useCreateAnforderung(projectId);
  const updateMutation = useUpdateAnforderung(projectId);
  const kategorien = useAnforderungKategorieNamen();

  const [form, setForm] = useState<FormData>(INITIAL);
  // Aktuellen (evtl. nicht mehr gelisteten) Wert immer als Option behalten.
  const kategorieOptions = form.kategorie && !kategorien.includes(form.kategorie)
    ? [form.kategorie, ...kategorien]
    : kategorien;

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initialData) {
      setForm({
        role: (initialData.role as string) ?? '',
        ziel: (initialData.ziel as string) ?? '',
        nutzen: (initialData.nutzen as string) ?? '',
        kategorie: (initialData.kategorie as string) ?? '',
        story_points: initialData.story_points != null ? String(initialData.story_points) : '',
        status: (initialData.status as typeof STATUSES[number]) ?? 'Neu',
      });
    } else {
      setForm(INITIAL);
    }
  }, [open, mode, initialData]);

  const handleSave = async () => {
    if (!form.ziel.trim()) {
      notify(tc('errors.requiredFields', 'Pflichtfelder ausfüllen'), 'warning');
      return;
    }

    const payload: Record<string, unknown> = {
      role: form.role.trim() || null,
      ziel: form.ziel.trim(),
      nutzen: form.nutzen.trim() || null,
      kategorie: form.kategorie || null,
      story_points: form.story_points.trim() ? Number(form.story_points) : null,
      status: form.status,
    };

    try {
      if (mode === 'edit' && initialData?.id) {
        await updateMutation.mutateAsync({ id: initialData.id as string, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      notify(tc('status.saved', 'Gespeichert'), 'success');
      onClose();
    } catch (err) {
      notify(formatApiError(err), 'error');
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const update = <K extends keyof FormData>(key: K, value: FormData[K]) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <Dialog open={open} onClose={() => !isSaving && onClose()} maxWidth="sm" fullWidth>
      <AppDialogTitle>
        {mode === 'edit'
          ? t('anforderungen.editTitle', 'Anforderung bearbeiten')
          : t('anforderungen.createTitle', 'Neue Anforderung')}
      </AppDialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label={t('anforderungen.role', 'Rolle')}
            value={form.role}
            onChange={(e) => update('role', e.target.value)}
            placeholder={t('anforderungen.rolePlaceholder', 'Als ...')}
            fullWidth
          />

          <TextField
            required
            label={t('anforderungen.ziel', 'Ziel')}
            value={form.ziel}
            onChange={(e) => update('ziel', e.target.value)}
            placeholder={t('anforderungen.zielPlaceholder', 'möchte ich ...')}
            fullWidth
            multiline
          />

          <TextField
            label={t('anforderungen.nutzen', 'Nutzen')}
            value={form.nutzen}
            onChange={(e) => update('nutzen', e.target.value)}
            placeholder={t('anforderungen.nutzenPlaceholder', 'damit ...')}
            multiline
            rows={2}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>{t('anforderungen.kategorie', 'Kategorie')}</InputLabel>
            <Select
              value={form.kategorie}
              label={t('anforderungen.kategorie', 'Kategorie')}
              onChange={(e) => update('kategorie', e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {kategorieOptions.map((k) => (
                <MenuItem key={k} value={k}>{k}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t('anforderungen.storyPoints', 'Story Points')}
            value={form.story_points}
            onChange={(e) => update('story_points', e.target.value.replace(/\D/g, ''))}
            type="number"
            inputProps={{ min: 0, step: 1 }}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>{t('fields.state', 'Status')}</InputLabel>
            <Select
              value={form.status}
              label={t('fields.state', 'Status')}
              onChange={(e) => update('status', e.target.value as typeof STATUSES[number])}
            >
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>{t(`anforderungen.statuses.${s}`, s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>{tc('actions.cancel', 'Abbrechen')}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || !form.ziel.trim()}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {tc('actions.save', 'Speichern')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
